import React, { ComponentProps, useCallback, useContext, useMemo } from 'react'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import TextLink from 'LuxKit/TextLink'
import ModalContext from 'contexts/ModalContext'
import TasteOfGoldLearnMoreModal from './TasteOfGoldLearnMoreModal'
import { TasteOfGoldGoldLabel, TasteOfGoldIconCircle } from './TasteOfGoldPromoStyles'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'

type BodyTextVariant = ComponentProps<typeof BodyText>['variant']
type TextLinkSize = ComponentProps<typeof TextLink>['size']

interface Props {
  /**
   * Controls the body text and link size.
   * @default 'small'
   */
  size?: BodyTextVariant
  /**
   * Layout variant for the promo content.
   * - 'vertical': label on top, icon on the right (used in drawer)
   * - 'horizontal': icon on left, label inline with title (used on landing page)
   * @default 'vertical'
   */
  layout?: 'vertical' | 'horizontal'
  /**
   * Whether to stop click propagation on the Details link.
   * Needed when rendered inside a parent Clickable (e.g. account drawer).
   */
  stopPropagation?: boolean
  /**
   * Controls the description copy variant.
   * - 'general': "...for your next international escape." (default, used on loyalty pages)
   * - 'offerPage': "...when you book an eligible package." (used on offer pages)
   * @default 'general'
   */
  context?: 'general' | 'offerPage'
}

const BODY_TO_LINK_SIZE: Record<string, TextLinkSize> = {
  small: 'small',
  medium: 'medium',
  large: 'large',
}

function TasteOfGoldPromoContent(props: Props) {
  const {
    size = 'small',
    layout = 'vertical',
    stopPropagation = false,
    context = 'general',
  } = props
  const showModal = useContext(ModalContext)
  const linkSize = BODY_TO_LINK_SIZE[size] ?? 'small'

  const openLearnMore = useCallback((event: React.MouseEvent) => {
    if (stopPropagation) {
      event.preventDefault()
      event.stopPropagation()
    }
    showModal(<TasteOfGoldLearnMoreModal />)
  }, [showModal, stopPropagation])

  const textContent = useMemo(() => (
    <VerticalSpacer gap={4}>
      {layout === 'horizontal' && <Group direction="horizontal" verticalAlign="center" gap={8} wrap="wrap">
        <BodyText variant="medium" weight="bold">A Special Gift For You</BodyText>
        <TasteOfGoldGoldLabel>Limited time only</TasteOfGoldGoldLabel>
      </Group>}
      {layout !== 'horizontal' && <BodyText variant="medium" weight="bold">A Special Gift For You</BodyText>}
      <BodyText variant={size}>
        You&rsquo;re invited to experience {getLoyaltyProgramDisplayName()} {getLoyaltyTierDisplayName('gold')}. Enjoy a free airport transfer {context === 'offerPage' ? 'when you book an eligible package' : 'for your next international escape'}.{' '}
        <TextLink size={linkSize} underline onClick={openLearnMore}>Details</TextLink>
      </BodyText>
    </VerticalSpacer>
  ), [layout, size, linkSize, openLearnMore, context])

  if (layout === 'horizontal') {
    return (
      <Group direction="horizontal" verticalAlign="start" gap={12}>
        <TasteOfGoldIconCircle>
          <span><LineTaxiIcon size="M" /></span>
        </TasteOfGoldIconCircle>
        {textContent}
      </Group>
    )
  }

  return (
    <VerticalSpacer gap={8}>
      <div><TasteOfGoldGoldLabel>Limited time only</TasteOfGoldGoldLabel></div>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" gap={16}>
        {textContent}
        <TasteOfGoldIconCircle>
          <span><LineTaxiIcon size="M" /></span>
        </TasteOfGoldIconCircle>
      </Group>
    </VerticalSpacer>
  )
}

export default TasteOfGoldPromoContent
